/**
 * Character limits and AdForge's one character-counting rule.
 *
 * This is a deliberate mirror of the frozen `adforge/schemas/limits.py` (W0). The
 * console is a client, so it cannot import Python: it repeats the constants and the
 * counting rule *verbatim*, and `limits.test.ts` pins each behaviour to ground truth
 * produced by the frozen implementation. Two rules matter and are followed here:
 *
 *  1. Nothing in AdForge truncates an ad asset. The editor refuses over-limit text and
 *     shows the counted width; it never silently shortens a headline.
 *  2. Counting is by code point, except double-width (East Asian Wide/Fullwidth) code
 *     points, which count as two — the same rule both ad platforms use.
 */
import { EAST_ASIAN_WIDE_RANGES } from './east-asian-width'

export const RSA_HEADLINE_MAX_CHARS = 30
export const RSA_DESCRIPTION_MAX_CHARS = 90
export const RSA_PATH_MAX_CHARS = 15
export const RSA_MIN_HEADLINES = 3
export const RSA_MAX_HEADLINES = 15
export const RSA_MIN_DESCRIPTIONS = 2
export const RSA_MAX_DESCRIPTIONS = 4
export const RSA_MAX_HEADLINE_POSITIONS = 15
export const RSA_MAX_DESCRIPTION_POSITIONS = 4

export const KEYWORD_MAX_CHARS = 80
export const KEYWORD_MAX_WORDS = 10

export const URL_MAX_CHARS = 2048
export const NAME_MAX_CHARS = 128

const PATH_PATTERN = /^[A-Za-z0-9_-]+$/

/** Control characters, zero-width and BOM: rejected outright, never stripped. */
const FORBIDDEN_CONTROL = /[\u0000-\u001F\u007F\u200B\uFEFF]/

function isWideCodePoint(cp: number): boolean {
  let low = 0
  let high = EAST_ASIAN_WIDE_RANGES.length - 1
  while (low <= high) {
    const mid = (low + high) >> 1
    const entry = EAST_ASIAN_WIDE_RANGES[mid]!
    if (cp < entry[0]) high = mid - 1
    else if (cp > entry[1]) low = mid + 1
    else return true
  }
  return false
}

/** Count `text` the way the ad platforms count it (double-width = 2). */
export function countCharacters(text: string): number {
  let total = 0
  for (const ch of text) {
    total += isWideCodePoint(ch.codePointAt(0)!) ? 2 : 1
  }
  return total
}

/** Whitespace-delimited word count, as the platforms count keyword words. */
export function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length
}

function commonTextError(text: string, label: string): string | null {
  if (text === '') return `${label} must not be empty`
  if (text !== text.trim()) {
    return `${label} has leading or trailing whitespace (got ${JSON.stringify(text)})`
  }
  if (FORBIDDEN_CONTROL.test(text)) {
    return `${label} contains characters that are not allowed in ad text`
  }
  return null
}

function limitError(text: string, limit: number, label: string): string | null {
  const counted = countCharacters(text)
  if (counted > limit) {
    const raw = [...text].length
    const detail =
      counted === raw
        ? `counted width ${counted} characters`
        : `counted width ${counted} characters (${raw} code points; double-width characters count as two)`
    return `${label} is ${detail}, limit is ${limit}`
  }
  return null
}

export function headlineError(text: string): string | null {
  const label = 'RSA headline'
  return commonTextError(text, label) ?? limitError(text, RSA_HEADLINE_MAX_CHARS, label)
}

export function descriptionError(text: string): string | null {
  const label = 'RSA description'
  return commonTextError(text, label) ?? limitError(text, RSA_DESCRIPTION_MAX_CHARS, label)
}

export function pathError(text: string): string | null {
  const label = 'RSA path'
  const problem =
    commonTextError(text, label) ?? limitError(text, RSA_PATH_MAX_CHARS, label)
  if (problem) return problem
  if (!PATH_PATTERN.test(text)) {
    return `${label} may only contain letters, digits, '-' and '_' (no spaces)`
  }
  return null
}

export function keywordTextError(text: string): string | null {
  const label = 'keyword text'
  const problem = commonTextError(text, label) ?? limitError(text, KEYWORD_MAX_CHARS, label)
  if (problem) return problem
  const words = countWords(text)
  if (words > KEYWORD_MAX_WORDS) {
    return `${label} has ${words} words, both platforms limit keywords to ${KEYWORD_MAX_WORDS}`
  }
  return null
}

export function nameError(text: string): string | null {
  const label = 'name'
  return commonTextError(text, label) ?? limitError(text, NAME_MAX_CHARS, label)
}

export function urlError(url: string): string | null {
  if (url.length > URL_MAX_CHARS) {
    return `final URL is ${url.length} characters, both platforms limit URLs to ${URL_MAX_CHARS}`
  }
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `final URL must be absolute http(s), got ${JSON.stringify(url)}`
  }
  if (url.includes(' ')) return 'final URL must not contain spaces'
  return null
}

/** Duplicate assets inside one RSA are rejected by both platforms. */
export function duplicateAssets(assets: readonly string[]): number[] {
  const seen = new Map<string, number>()
  const dupes: number[] = []
  assets.forEach((text, index) => {
    const key = text.toLowerCase()
    if (seen.has(key)) dupes.push(index)
    else seen.set(key, index)
  })
  return dupes
}

/**
 * Non-fatal Ad Strength advice, mirroring `ResponsiveSearchAdSpec.pinning_advisories`
 * in the frozen schema. The editor surfaces these as advisories, never as errors.
 */
export function pinningAdvisories(input: {
  headlines: number
  pinnedHeadlines: number
  path1: string | null
}): string[] {
  const notes: string[] = []
  if (input.pinnedHeadlines > 0 && input.pinnedHeadlines === input.headlines) {
    notes.push(
      'every headline is pinned; both platforms rotate less and may serve a worse combination — leave at least one headline unpinned',
    )
  }
  if (input.pinnedHeadlines > 3) {
    notes.push(`${input.pinnedHeadlines} headlines are pinned; more than 3 is unusual`)
  }
  if (input.path1 === null) notes.push('no display path set; both paths improve click-through')
  return notes
}
